/**
 * Comparative-period arithmetic for the financial statements — the client twin of the backend's
 * ReportPeriods (the PDF/XLSX exports compute the same periods server-side). Dates are plain
 * yyyy-MM-dd strings handled as calendar dates (never through Date/UTC conversions).
 */
export type CompareMode = 'NONE' | 'PREVIOUS' | 'LAST_YEAR' | 'BOTH';

export interface Period { from: string; to: string }

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const parse = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return { y, m, d };
};
const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const toEpochDay = (s: string) => {
    const { y, m, d } = parse(s);
    return Math.round(Date.UTC(y, m - 1, d) / 86400000);
};
const fromEpochDay = (n: number) => {
    const dt = new Date(n * 86400000);
    return iso(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
};
const shiftMonths = (y: number, m: number, delta: number) => {
    const idx = y * 12 + (m - 1) + delta;
    return { y: Math.floor(idx / 12), m: (idx % 12) + 1 };
};

export const isWholeMonths = (from: string, to: string): boolean => {
    const f = parse(from);
    const t = parse(to);
    return f.d === 1 && t.d === daysInMonth(t.y, t.m);
};

/** The period of the same length immediately before [from, to]; whole months shift by months. */
export function previousPeriod(from: string, to: string): Period {
    if (to < from) throw new Error('to must not be before from');
    if (isWholeMonths(from, to)) {
        const f = parse(from);
        const t = parse(to);
        const months = (t.y * 12 + t.m) - (f.y * 12 + f.m) + 1;
        const start = shiftMonths(f.y, f.m, -months);
        return { from: iso(start.y, start.m, 1), to: fromEpochDay(toEpochDay(from) - 1) };
    }
    const days = toEpochDay(to) - toEpochDay(from) + 1;
    const prevTo = toEpochDay(from) - 1;
    return { from: fromEpochDay(prevTo - days + 1), to: fromEpochDay(prevTo) };
}

/** The same dates one year earlier (month ends kept for whole-month ranges, 29 Feb clamps to 28 Feb). */
export function sameLastYear(from: string, to: string): Period {
    if (to < from) throw new Error('to must not be before from');
    const f = parse(from);
    const t = parse(to);
    if (isWholeMonths(from, to)) {
        return { from: iso(f.y - 1, f.m, 1), to: iso(t.y - 1, t.m, daysInMonth(t.y - 1, t.m)) };
    }
    const clamp = (p: { y: number; m: number; d: number }) =>
        iso(p.y - 1, p.m, Math.min(p.d, daysInMonth(p.y - 1, p.m)));
    return { from: clamp(f), to: clamp(t) };
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export const variance = (current: number, comparative: number): number =>
    round2((current ?? 0) - (comparative ?? 0));

/** Variance relative to the comparative's magnitude (percent, 2 dp); null when the comparative is zero. */
export const variancePercent = (current: number, comparative: number): number | null => {
    const base = comparative ?? 0;
    if (Math.abs(base) < 0.005) return null;
    return round2(((current ?? 0) - base) * 100 / Math.abs(base));
};

export const wantsPrevious = (m: CompareMode) => m === 'PREVIOUS' || m === 'BOTH';
export const wantsLastYear = (m: CompareMode) => m === 'LAST_YEAR' || m === 'BOTH';

/** The calendar day before an ISO date (e.g. the previous financial year end). */
export const dayBefore = (isoDate: string): string => fromEpochDay(toEpochDay(isoDate) - 1);
